import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  setDoc,
  query, 
  where, 
  orderBy,
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from '../config/firebase';

/**
 * 🏗️ CMT Management Service
 * 
 * Handles multi-CMT operations while maintaining backward compatibility
 * with existing single-CMT organizations.
 * 
 * @author Crisis Compass Development Team
 * @version 1.0.0 - Phase 1 MVP Implementation
 */

// CMT Types for standardization (Enhanced for Hierarchy)
export const CMT_TYPES = {
  GLOBAL: 'global',       // NEW: Global/Parent CMT
  PRIMARY: 'primary',     // Legacy support
  REGIONAL: 'regional', 
  FUNCTIONAL: 'functional',
  DIVISION: 'division',   // NEW: Divisional CMTs
  CUSTOM: 'custom'
};

// CMT Hierarchy Levels
export const CMT_LEVELS = {
  GLOBAL: 0,     // Global/Parent CMT
  CHILD: 1,      // Direct child of Global
  SUBCHILD: 2    // Child of child (future expansion)
};

// CMT Roles for validation
export const CMT_ROLES = [
  'Leader',
  'Alternate', 
  'Comms/Media',
  'Security',
  'IT',
  'HR',
  'Legal',
  'Finance',
  'Operations',
  'Other'
];

// Cache for performance optimization
const cmtCache = new Map();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * 🔧 CMT Service Implementation
 */
export const cmtService = {
  
  /**
   * 📋 Get all CMTs for an organization
   * @param {string} orgId - Organization ID
   * @returns {Promise<Array>} Array of CMT objects
   */
  async getOrganizationCMTs(orgId) {
    try {
      if (!orgId) {
        throw new Error('Organization ID is required');
      }

      const cacheKey = `cmts:${orgId}`;
      const cached = this._getFromCache(cacheKey);
      if (cached) return cached;

      // Query CMT subcollection
      const cmtsRef = collection(db, `c3_organisations/${orgId}/cmts`);
      const q = query(cmtsRef, orderBy('createdAt', 'asc'));
      
      const querySnapshot = await getDocs(q);
      const cmts = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          // Safe timestamp handling - only call toDate() if it's actually a Timestamp
          createdAt: data.createdAt && typeof data.createdAt.toDate === 'function' 
            ? data.createdAt.toDate() 
            : data.createdAt,
          updatedAt: data.updatedAt && typeof data.updatedAt.toDate === 'function' 
            ? data.updatedAt.toDate() 
            : data.updatedAt
        };
      });

      // 🔄 BACKWARD COMPATIBILITY: If no CMTs exist, check for legacy CMT
      if (cmts.length === 0) {
        const legacyCmt = await this._createDefaultCMTFromLegacy(orgId);
        if (legacyCmt) {
          cmts.push(legacyCmt);
        }
      }

      this._setCache(cacheKey, cmts);
      return cmts;
    } catch (error) {
      console.error('Error getting organization CMTs:', error);
      throw new Error(`Failed to get CMTs: ${error.message}`);
    }
  },

  /**
   * 🔎 Flatten members from an organization document
   * Prefers multiCmtMembers; falls back to legacy cmtMembers only if necessary.
   * Returns an array of members with cmtId included for each entry.
   */
  flattenMembersFromOrgDoc(orgData) {
    try {
      const multi = orgData?.multiCmtMembers || {};
      const hasMulti = multi && typeof multi === 'object' && Object.keys(multi).length > 0;
      let flattened = [];

      if (hasMulti) {
        Object.entries(multi).forEach(([cmtId, list]) => {
          if (Array.isArray(list)) {
            list.forEach(m => {
              const rawRole = m.cmtRole || m.role || m.assignedRole || m.assignmentRole || m.cmt_role || m.roleName || m.cmtFunctionalRole || m.functionalRole || 'member';
              flattened.push({
                email: (m.email || '').toLowerCase(),
                fullName: m.fullName || '',
                phone: m.phone || '',
                cmtRole: rawRole, // keep literal
                cmtId
              });
            });
          }
        });
        console.log('CMT_MONITOR: flatten', {
          source: 'multiCmtMembers',
          members: flattened.length,
          cmtKeys: Object.keys(multi)
        });
        return flattened;
      }

      // Legacy fallback
      const legacy = Array.isArray(orgData?.cmtMembers) ? orgData.cmtMembers : [];
      if (legacy.length > 0) {
        console.warn('⚠️ Using legacy cmtMembers fallback during flatten');
        flattened = legacy.map(m => {
          const rawRole = m.cmtRole || m.role || m.assignedRole || m.assignmentRole || m.cmt_role || m.roleName || m.cmtFunctionalRole || m.functionalRole || 'member';
          return {
            email: (m.email || '').toLowerCase(),
            fullName: m.fullName || '',
            phone: m.phone || '',
            cmtRole: rawRole, // keep literal
            cmtId: m.cmtId || 'default_cmt'
          };
        });
        console.log('CMT_MONITOR: flatten', {
          source: 'cmtMembers',
          members: flattened.length
        });
      }
      return flattened;
    } catch (e) {
      console.error('❌ flattenMembersFromOrgDoc error:', e);
      return [];
    }
  },

  /**
   * 📥 Fetch org doc and return flattened members (preferred source: multiCmtMembers)
   */
  async getFlattenedMembers(orgId) {
    const orgRef = doc(db, 'c3_organisations', orgId);
    const snap = await getDoc(orgRef);
    const data = snap.exists() ? snap.data() : {};
    return this.flattenMembersFromOrgDoc(data);
  },

  /**
   * 🆔 Get specific CMT by ID
   * @param {string} orgId - Organization ID
   * @param {string} cmtId - CMT ID
   * @returns {Promise<Object>} CMT object
   */
  async getCMT(orgId, cmtId) {
    try {
      if (!orgId || !cmtId) {
        throw new Error('Organization ID and CMT ID are required');
      }

      const docRef = doc(db, `c3_organisations/${orgId}/cmts`, cmtId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        throw new Error('CMT not found');
      }

      const data = docSnap.data();
      return {
        id: docSnap.id,
        ...data,
        // Safe timestamp handling - only call toDate() if it's actually a Timestamp
        createdAt: data.createdAt && typeof data.createdAt.toDate === 'function' 
          ? data.createdAt.toDate() 
          : data.createdAt,
        updatedAt: data.updatedAt && typeof data.updatedAt.toDate === 'function' 
          ? data.updatedAt.toDate() 
          : data.updatedAt
      };
    } catch (error) {
      console.error('Error getting CMT:', error);
      throw new Error(`Failed to get CMT: ${error.message}`);
    }
  },

  /**
   * ➕ Create new CMT
   * @param {string} orgId - Organization ID
   * @param {Object} cmtData - CMT data
   * @returns {Promise<Object>} Created CMT object
   */
  async createCMT(orgId, cmtData) {
    try {
      if (!orgId || !cmtData) {
        throw new Error('Organization ID and CMT data are required');
      }

      // Validate required fields
      this._validateCMTData(cmtData);

      // Check if this would be the first CMT - make it default
      const existingCMTs = await this.getOrganizationCMTs(orgId);
      const isFirstCMT = existingCMTs.length === 0;

      // 🏗️ PHASE 1: Enhanced schema with hierarchy support
      const isGlobalCMT = cmtData.type === CMT_TYPES.GLOBAL || cmtData.isGlobalCmt === true;
      const parentCmtId = cmtData.parentCmtId || null;
      const level = isGlobalCMT ? CMT_LEVELS.GLOBAL : (parentCmtId ? CMT_LEVELS.CHILD : CMT_LEVELS.GLOBAL);

      const cmtToCreate = {
        name: cmtData.name,
        type: cmtData.type || CMT_TYPES.PRIMARY,
        description: cmtData.description || '',
        
        // 🆕 HIERARCHY FIELDS (Phase 1)
        parentCmtId: parentCmtId,
        isGlobalCmt: isGlobalCMT,
        childCmtIds: cmtData.childCmtIds || [],
        level: level,
        
        // Legacy fields (maintained for backward compatibility)
        isDefault: isFirstCMT || cmtData.isDefault || false,
        isActive: cmtData.isActive !== false, // Default to true
        members: cmtData.members || [],
        permissions: {
          canManageIncidents: true,
          canManageTasks: true,
          canManageMembers: false, // Only admins can manage members
          canViewReports: true,
          
          // 🆕 HIERARCHY PERMISSIONS (Phase 1)
          canViewAllCMTs: isGlobalCMT, // Global CMT sees all child CMTs
          canCoordinateAcrossCMTs: isGlobalCMT, // Global CMT can coordinate
          
          ...cmtData.permissions
        },
        metadata: cmtData.metadata || {},
        createdAt: serverTimestamp(),
        createdBy: cmtData.createdBy,
        updatedAt: serverTimestamp(),
        updatedBy: cmtData.createdBy
      };

      // If this is being set as default, unset other defaults
      if (cmtToCreate.isDefault && !isFirstCMT) {
        await this._unsetOtherDefaults(orgId);
      }

      // Use the custom ID from cmtData instead of auto-generated ID
      const cmtId = cmtData.id || `cmt_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const cmtDocRef = doc(db, `c3_organisations/${orgId}/cmts`, cmtId);
      await setDoc(cmtDocRef, cmtToCreate);

      // 🔗 PHASE 1: Update parent-child relationships
      if (parentCmtId) {
        await this._addChildToParent(orgId, parentCmtId, cmtId);
      }

      // Clear cache
      this._clearCacheForOrg(orgId);

      return {
        id: cmtId,
        ...cmtToCreate,
        createdAt: new Date(),
        updatedAt: new Date()
      };
    } catch (error) {
      console.error('Error creating CMT:', error);
      throw new Error(`Failed to create CMT: ${error.message}`);
    }
  },

  /**
   * 🔄 Update existing CMT
   * @param {string} orgId - Organization ID
   * @param {string} cmtId - CMT ID
   * @param {Object} updates - Fields to update
   * @returns {Promise<Object>} Updated CMT object
   */
  async updateCMT(orgId, cmtId, updates) {
    try {
      if (!orgId || !cmtId || !updates) {
        throw new Error('Organization ID, CMT ID, and updates are required');
      }

      const docRef = doc(db, `c3_organisations/${orgId}/cmts`, cmtId);
      
      // Validate CMT exists
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) {
        throw new Error('CMT not found');
      }

      // If setting as default, unset other defaults
      if (updates.isDefault === true) {
        await this._unsetOtherDefaults(orgId, cmtId);
      }

      const updateData = {
        ...updates,
        updatedAt: serverTimestamp(),
        updatedBy: updates.updatedBy
      };

      // Remove undefined values
      Object.keys(updateData).forEach(key => {
        if (updateData[key] === undefined) {
          delete updateData[key];
        }
      });

      await updateDoc(docRef, updateData);

      // Clear cache
      this._clearCacheForOrg(orgId);

      return this.getCMT(orgId, cmtId);
    } catch (error) {
      console.error('Error updating CMT:', error);
      throw new Error(`Failed to update CMT: ${error.message}`);
    }
  },

  /**
   * 🗑️ Delete CMT (soft delete by setting inactive)
   * @param {string} orgId - Organization ID
   * @param {string} cmtId - CMT ID
   * @param {string} deletedBy - User ID performing deletion
   * @returns {Promise<boolean>} Success status
   */
  async deleteCMT(orgId, cmtId, deletedBy) {
    try {
      if (!orgId || !cmtId) {
        throw new Error('Organization ID and CMT ID are required');
      }

      // Get CMT to check if it's default
      const cmt = await this.getCMT(orgId, cmtId);
      
      if (cmt.isDefault) {
        throw new Error('Cannot delete the default CMT. Please set another CMT as default first.');
      }

      // Soft delete by setting inactive
      await this.updateCMT(orgId, cmtId, {
        isActive: false,
        deletedAt: new Date(),
        deletedBy: deletedBy,
        updatedBy: deletedBy
      });

      return true;
    } catch (error) {
      console.error('Error deleting CMT:', error);
      throw new Error(`Failed to delete CMT: ${error.message}`);
    }
  },

  /**
   * 👥 Add member to CMT
   * @param {string} orgId - Organization ID
   * @param {string} cmtId - CMT ID
   * @param {Object} memberData - Member data
   * @returns {Promise<Object>} Updated CMT object
   */
  async addCMTMember(orgId, cmtId, memberData) {
    try {
      const cmt = await this.getCMT(orgId, cmtId);
      
      // Check if member already exists
      const existingMember = cmt.members.find(m => 
        m.userId === memberData.userId || m.email === memberData.email
      );
      
      if (existingMember) {
        throw new Error('Member already exists in this CMT');
      }

      const newMember = {
        userId: memberData.userId,
        fullName: memberData.fullName,
        email: memberData.email,
        cmtRole: memberData.cmtRole,
        phone: memberData.phone || '',
        isActive: true,
        assignedAt: new Date(),
        assignedBy: memberData.assignedBy
      };

      const updatedMembers = [...cmt.members, newMember];

      return this.updateCMT(orgId, cmtId, {
        members: updatedMembers,
        updatedBy: memberData.assignedBy
      });
    } catch (error) {
      console.error('Error adding CMT member:', error);
      throw new Error(`Failed to add CMT member: ${error.message}`);
    }
  },

  /**
   * 🔄 Update CMT member
   * @param {string} orgId - Organization ID
   * @param {string} cmtId - CMT ID
   * @param {string} userId - User ID to update
   * @param {Object} updates - Member updates
   * @returns {Promise<Object>} Updated CMT object
   */
  async updateCMTMember(orgId, cmtId, userId, updates) {
    try {
      const cmt = await this.getCMT(orgId, cmtId);
      
      const memberIndex = cmt.members.findIndex(m => m.userId === userId);
      if (memberIndex === -1) {
        throw new Error('Member not found in CMT');
      }

      const updatedMembers = [...cmt.members];
      updatedMembers[memberIndex] = {
        ...updatedMembers[memberIndex],
        ...updates,
        updatedAt: new Date(),
        updatedBy: updates.updatedBy
      };

      return this.updateCMT(orgId, cmtId, {
        members: updatedMembers,
        updatedBy: updates.updatedBy
      });
    } catch (error) {
      console.error('Error updating CMT member:', error);
      throw new Error(`Failed to update CMT member: ${error.message}`);
    }
  },

  /**
   * ➖ Remove member from CMT
   * @param {string} orgId - Organization ID
   * @param {string} cmtId - CMT ID
   * @param {string} userId - User ID to remove
   * @param {string} removedBy - User ID performing removal
   * @returns {Promise<Object>} Updated CMT object
   */
  async removeCMTMember(orgId, cmtId, userId, removedBy) {
    try {
      const cmt = await this.getCMT(orgId, cmtId);
      
      const updatedMembers = cmt.members.filter(m => m.userId !== userId);

      return this.updateCMT(orgId, cmtId, {
        members: updatedMembers,
        updatedBy: removedBy
      });
    } catch (error) {
      console.error('Error removing CMT member:', error);
      throw new Error(`Failed to remove CMT member: ${error.message}`);
    }
  },

  /**
   * 🎯 Get default CMT for organization
   * @param {string} orgId - Organization ID
   * @returns {Promise<Object|null>} Default CMT or null
   */
  async getDefaultCMT(orgId) {
    try {
      const cmts = await this.getOrganizationCMTs(orgId);
      return cmts.find(cmt => cmt.isDefault) || cmts[0] || null;
    } catch (error) {
      console.error('Error getting default CMT:', error);
      return null;
    }
  },

  /**
   * 🔄 Set CMT as default
   * @param {string} orgId - Organization ID
   * @param {string} cmtId - CMT ID to set as default
   * @param {string} updatedBy - User ID performing update
   * @returns {Promise<Object>} Updated CMT object
   */
  async setDefaultCMT(orgId, cmtId, updatedBy) {
    try {
      // First unset other defaults
      await this._unsetOtherDefaults(orgId, cmtId);
      
      // Then set this one as default
      return this.updateCMT(orgId, cmtId, {
        isDefault: true,
        updatedBy: updatedBy
      });
    } catch (error) {
      console.error('Error setting default CMT:', error);
      throw new Error(`Failed to set default CMT: ${error.message}`);
    }
  },

  // ==========================================
  // 🔧 PRIVATE HELPER METHODS
  // ==========================================

  /**
   * 🔄 Create default CMT from legacy organization data
   * @private
   */
  async _createDefaultCMTFromLegacy(orgId) {
    try {
      // Check if organization has legacy cmtMembers
      const orgRef = doc(db, 'c3_organisations', orgId);
      const orgSnap = await getDoc(orgRef);
      
      if (!orgSnap.exists()) {
        return null;
      }

      const orgData = orgSnap.data();
      const legacyMembers = orgData.cmtMembers || [];

      if (legacyMembers.length === 0) {
        return null;
      }

      console.log(`🔄 Creating default CMT from legacy data for org ${orgId}`);

      // Convert legacy members to new format
      const members = legacyMembers.map(member => ({
        userId: member.userId || `legacy-${member.email}`, // Generate if missing
        fullName: member.fullName,
        email: member.email,
        cmtRole: member.cmtRole,
        phone: member.phone || '',
        isActive: true,
        assignedAt: new Date(),
        assignedBy: 'system-migration'
      }));

      // Create default CMT
      const defaultCmt = await this.createCMT(orgId, {
        name: 'Main Crisis Team',
        type: CMT_TYPES.PRIMARY,
        description: 'Default CMT created from legacy data',
        isDefault: true,
        members: members,
        createdBy: 'system-migration'
      });

      console.log(`✅ Created default CMT ${defaultCmt.id} for org ${orgId}`);
      return defaultCmt;
    } catch (error) {
      console.error('Error creating default CMT from legacy:', error);
      return null;
    }
  },

  // ===============================
  // 🏗️ PHASE 1: HIERARCHY FUNCTIONS
  // ===============================

  /**
   * 🌍 Get Global CMT for organization
   * @param {string} orgId - Organization ID
   * @returns {Promise<Object|null>} Global CMT or null if none exists
   */
  async getGlobalCMT(orgId) {
    try {
      const cmts = await this.getOrganizationCMTs(orgId);
      return cmts.find(cmt => cmt.isGlobalCmt === true) || null;
    } catch (error) {
      console.error('Error getting Global CMT:', error);
      throw new Error(`Failed to get Global CMT: ${error.message}`);
    }
  },

  /**
   * 👶 Get child CMTs for a parent CMT
   * @param {string} orgId - Organization ID
   * @param {string} parentCmtId - Parent CMT ID
   * @returns {Promise<Array>} Array of child CMT objects
   */
  async getChildCMTs(orgId, parentCmtId) {
    try {
      const cmts = await this.getOrganizationCMTs(orgId);
      return cmts.filter(cmt => cmt.parentCmtId === parentCmtId);
    } catch (error) {
      console.error('Error getting child CMTs:', error);
      throw new Error(`Failed to get child CMTs: ${error.message}`);
    }
  },

  /**
   * 🏗️ Get CMT hierarchy tree for organization
   * @param {string} orgId - Organization ID
   * @returns {Promise<Object>} Hierarchical CMT structure
   */
  async getCMTHierarchy(orgId) {
    try {
      const allCmts = await this.getOrganizationCMTs(orgId);
      const globalCmt = allCmts.find(cmt => cmt.isGlobalCmt === true);
      
      if (!globalCmt) {
        // No hierarchy - return flat structure
        return {
          hasHierarchy: false,
          globalCmt: null,
          childCmts: allCmts,
          structure: allCmts
        };
      }

      const childCmts = allCmts.filter(cmt => cmt.parentCmtId === globalCmt.id);
      
      return {
        hasHierarchy: true,
        globalCmt: {
          ...globalCmt,
          children: childCmts
        },
        childCmts: childCmts,
        structure: {
          global: globalCmt,
          children: childCmts
        }
      };
    } catch (error) {
      console.error('Error getting CMT hierarchy:', error);
      throw new Error(`Failed to get CMT hierarchy: ${error.message}`);
    }
  },

  /**
   * 🔗 Add child CMT ID to parent's childCmtIds array
   * @private
   */
  async _addChildToParent(orgId, parentCmtId, childCmtId) {
    try {
      const parentDocRef = doc(db, `c3_organisations/${orgId}/cmts`, parentCmtId);
      const parentDoc = await getDoc(parentDocRef);
      
      if (!parentDoc.exists()) {
        throw new Error('Parent CMT not found');
      }

      const parentData = parentDoc.data();
      const currentChildren = parentData.childCmtIds || [];
      
      if (!currentChildren.includes(childCmtId)) {
        await updateDoc(parentDocRef, {
          childCmtIds: [...currentChildren, childCmtId],
          updatedAt: serverTimestamp()
        });
      }
    } catch (error) {
      console.error('Error adding child to parent:', error);
      throw new Error(`Failed to update parent-child relationship: ${error.message}`);
    }
  },

  /**
   * 🔄 Unset default flag on other CMTs
   * @private
   */
  async _unsetOtherDefaults(orgId, excludeCmtId = null) {
    try {
      const cmts = await this.getOrganizationCMTs(orgId);
      const batch = writeBatch(db);

      cmts.forEach(cmt => {
        if (cmt.id !== excludeCmtId && cmt.isDefault) {
          const cmtRef = doc(db, `c3_organisations/${orgId}/cmts`, cmt.id);
          batch.update(cmtRef, { 
            isDefault: false,
            updatedAt: serverTimestamp()
          });
        }
      });

      if (batch._mutations.length > 0) {
        await batch.commit();
      }
    } catch (error) {
      console.error('Error unsetting other defaults:', error);
    }
  },

  /**
   * ✅ Validate CMT data
   * @private
   */
  _validateCMTData(cmtData) {
    if (!cmtData.name || cmtData.name.trim().length === 0) {
      throw new Error('CMT name is required');
    }

    if (cmtData.type && !Object.values(CMT_TYPES).includes(cmtData.type)) {
      throw new Error('Invalid CMT type');
    }

    if (cmtData.members) {
      cmtData.members.forEach(member => {
        if (!member.fullName || !member.email) {
          throw new Error('Member full name and email are required');
        }
        if (member.cmtRole && !CMT_ROLES.includes(member.cmtRole)) {
          throw new Error(`Invalid CMT role: ${member.cmtRole}`);
        }
      });
    }
  },

  /**
   * 💾 Cache management
   * @private
   */
  _getFromCache(key) {
    const cached = cmtCache.get(key);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      return cached.data;
    }
    return null;
  },

  _setCache(key, data) {
    cmtCache.set(key, {
      data,
      timestamp: Date.now()
    });
  },

  _clearCacheForOrg(orgId) {
    const keysToDelete = [];
    for (const key of cmtCache.keys()) {
      if (key.includes(orgId)) {
        keysToDelete.push(key);
      }
    }
    keysToDelete.forEach(key => cmtCache.delete(key));
  },

   /**
    * 💾 Save CMT Data (Admin Interface)
    * 
    * Saves both CMT structure and member data:
    * - CMT structure to /cmts subcollection (individual docs)
    * - CMT members to organization.multiCmtMembers map (single source of truth)
    * - Also writes legacy organization.cmtMembers as a DERIVED flat list during transition
    * 
    * Read-after-write verification is performed by fetching the org doc and
    * returning the fresh server copy so callers can reseed UI state reliably.
    * 
    * @param {string} orgId - Organization ID
    * @param {Array} cmts - Array of CMT structure objects
    * @param {Array} members - Array of CMT member objects  
    * @param {string} updatedBy - User ID performing the update
    * @returns {Promise<Object>} Fresh organization document data after save
    */
  async saveCMTData(orgId, cmts, members, updatedBy = 'admin_user') {
    try {
      console.log('💾 Saving CMT data:', { orgId, cmtCount: cmts?.length, memberCount: members?.length });
      
      // Convert flat members array back to multiCmtMembers format for persistence
      const multiCmtMembers = {};
      if (members && Array.isArray(members)) {
        members.forEach(member => {
          if (member && member.cmtId) {
            if (!multiCmtMembers[member.cmtId]) {
              multiCmtMembers[member.cmtId] = [];
            }
            // Remove cmtId from the member object before storing
            const { cmtId, ...memberData } = member;
            multiCmtMembers[member.cmtId].push(memberData);
          }
        });
      }
      
      console.log('💾 Converting members to multiCmtMembers format:', { 
        flatMembers: members?.length || 0, 
        multiCmtMembers: Object.keys(multiCmtMembers).length,
        detailed: multiCmtMembers 
      });
      
      console.log('🚨 CRITICAL DEBUG - About to save to Firestore:', {
        orgId,
        cmtMembersCount: (members || []).length,
        multiCmtMembersKeys: Object.keys(multiCmtMembers),
        multiCmtMembersDetail: JSON.stringify(multiCmtMembers, null, 2)
      });
      
      // Derive legacy flat cmtMembers for compatibility from multiCmtMembers
      const derivedFlatMembers = Object.entries(multiCmtMembers).flatMap(([cmtId, list]) =>
        (Array.isArray(list) ? list : []).map(m => ({
          email: (m.email || '').toLowerCase(),
          fullName: m.fullName || '',
          phone: m.phone || '',
          cmtRole: m.cmtRole || 'member',
          cmtId
        }))
      );

      // Save CMT members to organization document
      const orgRef = doc(db, 'c3_organisations', orgId);
      await updateDoc(orgRef, {
        'multiCmtMembers': multiCmtMembers,    // Structured format is the source of truth
        'cmtMembers': derivedFlatMembers,      // Legacy flat list (derived)
        'updatedAt': serverTimestamp(),
        'updatedBy': updatedBy
      });
      
      // Save CMT structure to subcollection
      if (cmts && cmts.length > 0) {
        const batch = writeBatch(db);
        
        // Get existing CMTs to delete removed ones
        const existingCmts = await this.getOrganizationCMTs(orgId);
        const existingCmtIds = new Set(existingCmts.map(cmt => cmt.id));
        const newCmtIds = new Set(cmts.map(cmt => cmt.id));
        
        // Delete removed CMTs
        for (const existingCmtId of existingCmtIds) {
          if (!newCmtIds.has(existingCmtId)) {
            const cmtRef = doc(db, 'c3_organisations', orgId, 'cmts', existingCmtId);
            batch.delete(cmtRef);
            console.log(`🗑️ Deleting CMT: ${existingCmtId}`);
          }
        }
        
        // Add/update CMTs
        for (const cmt of cmts) {
          const cmtRef = doc(db, 'c3_organisations', orgId, 'cmts', cmt.id);
          // 🚨 FILTER OUT UNDEFINED VALUES TO PREVENT FIRESTORE ERROR
          const cleanCmt = Object.fromEntries(
            Object.entries(cmt).filter(([_, value]) => value !== undefined)
          );
          
          const cmtData = {
            ...cleanCmt,
            updatedAt: serverTimestamp(),
            updatedBy: updatedBy
          };
          batch.set(cmtRef, cmtData, { merge: true });
          console.log(`💾 Saving CMT: ${cmt.id} - ${cmt.name}`);
        }
        
        await batch.commit();
      }
      
      // Clear cache for this organization
      this._clearCacheForOrg(orgId);
      
      // Read-after-write: return fresh server copy
      const freshSnap = await getDoc(orgRef);
      const freshData = freshSnap.exists() ? freshSnap.data() : {};
      console.log(`✅ CMT data saved successfully for org: ${orgId}`);
      return freshData;
      
    } catch (error) {
      console.error('❌ Error saving CMT data:', error);
      throw new Error(`Failed to save CMT data: ${error.message}`);
    }
  }
};

export default cmtService;